import React from 'react';
import {
  AdminConnectPayoutDriverDto,
  AdminPayoutDashboardDto,
  createAdminDriverManualPayout,
  getAdminPayoutDashboard,
  refreshAdminDriverConnectStatus,
  useApiClient,
} from '../api/client';
import { EmptyRow, MetricCard, PageHeader, PageShell, StatusBadge } from '../components/ui';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

function formatMoney(cents: number) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
}

function formatName(user: { firstName?: string | null; lastName?: string | null; email: string }) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '-';
}

function statusTone(status: string) {
  switch (status) {
    case 'READY':
      return 'green' as const;
    case 'IN_REVIEW':
      return 'blue' as const;
    case 'PAYOUTS_DISABLED':
    case 'ONBOARDING_REQUIRED':
      return 'amber' as const;
    case 'NO_ACCOUNT':
      return 'red' as const;
    default:
      return 'slate' as const;
  }
}

const issueLabels: Record<string, string> = {
  CAPTURE_FAILED: 'Capture failed',
  CAPTURED_WITHOUT_CONNECT_ROUTING: 'Captured without Connect routing',
  DRIVER_CONNECT_INCOMPLETE: 'Driver Connect incomplete',
  DRIVER_CONNECT_IN_REVIEW: 'Driver Connect in review',
  CONNECT_STATUS_STALE: 'Connect status stale',
};

function issueClass(issue: string) {
  switch (issue) {
    case 'CAPTURE_FAILED':
    case 'CAPTURED_WITHOUT_CONNECT_ROUTING':
      return 'bg-red-50 text-red-800 ring-red-200';
    case 'DRIVER_CONNECT_INCOMPLETE':
    case 'DRIVER_CONNECT_IN_REVIEW':
    case 'CONNECT_STATUS_STALE':
      return 'bg-amber-50 text-amber-800 ring-amber-200';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
}

export const PayoutsPage: React.FC = () => {
  const { request } = useApiClient();
  const { showError, showSuccess } = useToast();
  const [dashboard, setDashboard] = React.useState<AdminPayoutDashboardDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshingDriverId, setRefreshingDriverId] = React.useState<string | null>(null);
  const [payoutingDriverId, setPayoutingDriverId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(await getAdminPayoutDashboard(request));
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to load Stripe Connect dashboard';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [request, showError]);

  React.useEffect(() => {
    load();
  }, [load]);

  async function refreshDriver(driverId: string) {
    setRefreshingDriverId(driverId);
    setError(null);
    try {
      setDashboard(await refreshAdminDriverConnectStatus(request, driverId));
      showSuccess('Stripe Connect status refreshed.');
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to refresh Stripe Connect status';
      setError(message);
      showError(message);
    } finally {
      setRefreshingDriverId(null);
    }
  }

  async function payoutDriver(driverId: string) {
    setPayoutingDriverId(driverId);
    setError(null);
    try {
      setDashboard(await createAdminDriverManualPayout(request, driverId));
      showSuccess('Manual Stripe payout created.');
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to create manual Stripe payout';
      setError(message);
      showError(message);
    } finally {
      setPayoutingDriverId(null);
    }
  }

  const drivers = dashboard?.drivers || [];
  const actionInProgress = !!refreshingDriverId || !!payoutingDriverId;

  return (
    <PageShell>
      <PageHeader
        title="Stripe Connect Payouts"
        description="Connect-first payout readiness for paid Mobie rides. Driver proceeds should route through Stripe destination charges when the driver account is ready."
        actions={<button className="btn btn-neutral" onClick={load} disabled={loading || actionInProgress}>Refresh</button>}
      />

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <div className="text-sm text-gray-500">Loading...</div>}

      {!loading && dashboard && (
        <>
          <div className="mb-6 grid gap-3 md:grid-cols-4 xl:grid-cols-6">
            <MetricCard label="Captured paid rides" value={dashboard.overview.capturedPaidRideBookings} tone="green" />
            <MetricCard label="Capture failed" value={dashboard.overview.captureFailedBookings} tone="red" />
            <MetricCard label="Connect ready" value={dashboard.overview.connectReadyDriverCount} tone="green" />
            <MetricCard label="Onboarding required" value={dashboard.overview.incompleteOnboardingDriverCount} tone="amber" />
            <MetricCard label="In review" value={dashboard.overview.inReviewDriverCount} tone="blue" />
            <MetricCard label="Payouts disabled" value={dashboard.overview.payoutsDisabledDriverCount} tone="amber" />
            <MetricCard label="Stale sync" value={dashboard.overview.staleConnectStatusDriverCount} tone="violet" />
            <MetricCard label="Expected Connect earnings" value={formatMoney(dashboard.overview.expectedConnectEarningsInCents)} tone="blue" />
            <MetricCard label="Blocked earnings" value={formatMoney(dashboard.overview.blockedConnectEarningsInCents)} tone="red" />
            <MetricCard label="Service fees earned" value={formatMoney(dashboard.overview.serviceFeesEarnedInCents)} tone="slate" />
            <MetricCard label="Routing issues" value={dashboard.overview.capturedWithoutConnectRoutingCount} tone="red" />
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
            <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
              <span>{drivers.length} Connect-relevant drivers</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Driver</th>
                    <th className="px-4 py-3 font-semibold">Connect account</th>
                    <th className="px-4 py-3 font-semibold">Readiness</th>
                    <th className="px-4 py-3 font-semibold">Paid rides</th>
                    <th className="px-4 py-3 font-semibold">Money</th>
                    <th className="px-4 py-3 font-semibold">Issues</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <DriverRow
                      key={driver.userId}
                      driver={driver}
                      refreshing={refreshingDriverId === driver.userId}
                      payouting={payoutingDriverId === driver.userId}
                      actionDisabled={actionInProgress}
                      onRefresh={() => refreshDriver(driver.userId)}
                      onPayout={() => payoutDriver(driver.userId)}
                    />
                  ))}
                  {drivers.length === 0 && <EmptyRow colSpan={7}>No Connect-relevant drivers found.</EmptyRow>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
};

function DriverRow({
  driver,
  refreshing,
  payouting,
  actionDisabled,
  onRefresh,
  onPayout,
}: {
  driver: AdminConnectPayoutDriverDto;
  refreshing: boolean;
  payouting: boolean;
  actionDisabled: boolean;
  onRefresh: () => void;
  onPayout: () => void;
}) {
  const canPayout = driver.connectStatus === 'READY' && !!driver.stripeConnectedAccountId;

  return (
    <tr className="border-t align-top">
      <td className="px-4 py-3">
        <div className="font-semibold text-gray-900">{formatName(driver)}</div>
        <div className="text-xs text-gray-500">{driver.email}</div>
        <div className="text-xs text-gray-500">{driver.userId}</div>
      </td>
      <td className="px-4 py-3">
        <div className="max-w-[190px] truncate font-mono text-xs text-gray-700">{driver.stripeConnectedAccountId || 'No account'}</div>
        <div className="mt-1 text-xs text-gray-500">Synced {formatMaybeDate(driver.stripeConnectLastSyncedAt)}</div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge tone={statusTone(driver.connectStatus)}>{driver.connectStatus}</StatusBadge>
        <div className="mt-2 grid gap-1 text-xs text-gray-600">
          <div>Details: {driver.stripeConnectDetailsSubmitted ? 'yes' : 'no'}</div>
          <div>Charges: {driver.stripeConnectChargesEnabled ? 'yes' : 'no'}</div>
          <div>Payouts: {driver.stripeConnectPayoutsEnabled ? 'yes' : 'no'}</div>
          <div>Review: {driver.stripeConnectReviewPending ? 'pending' : 'no'}</div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-gray-900">{driver.capturedPaidBookingCount} captured</div>
        <div className="text-xs text-red-700">{driver.captureFailedBookingCount} failed</div>
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-gray-900">{formatMoney(driver.expectedConnectEarningsInCents)}</div>
        <div className="text-xs text-gray-500">Expected Connect earnings</div>
        <div className="mt-1 font-semibold text-red-700">{formatMoney(driver.blockedConnectEarningsInCents)}</div>
        <div className="text-xs text-gray-500">Blocked by Connect state</div>
      </td>
      <td className="px-4 py-3">
        <div className="flex max-w-xs flex-wrap gap-1">
          {driver.issues.map((issue) => (
            <span key={issue} className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ${issueClass(issue)}`}>
              {issueLabels[issue] || issue}
            </span>
          ))}
          {driver.issues.length === 0 && <span className="text-xs text-gray-500">No issue</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-2">
          <button className="btn btn-success whitespace-nowrap" onClick={onPayout} disabled={actionDisabled || !canPayout}>
            {payouting ? 'Paying out...' : 'Pay out now'}
          </button>
          <button className="btn btn-neutral whitespace-nowrap" onClick={onRefresh} disabled={actionDisabled || !driver.stripeConnectedAccountId}>
            {refreshing ? 'Refreshing...' : 'Refresh Connect'}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default PayoutsPage;
